import React, { useEffect, useState } from 'react';
import { ArrowRight } from '../../components/Glyph';
import { dayShort } from '../../lib/when';
import { Card, Kind, Greeting, FourBar, Need, NeedsCard, HomeFrame, today, dueState, DueChip } from './parts';
import { countWaiting } from '../admin/Ordering/orderJobs';
import ReleaseNote from '../../components/ReleaseNote';

/*
  Admin Home.

  Built 5 September 2026 for "One shell, one menu, three homes", against the
  approved drawing in docs/design/group-c/ (signed off 31 August 2026).

  **This screen did not exist.** The app opened straight onto Inventory Counts,
  which is a page about counting rather than a place to land, and the word
  "dashboard" was retired for exactly that reason: three files were called
  dashboards and none of them was one.

  **Three blocks, and the third is the one worth arguing about.**

    1  The open cycle       what the quarter is doing, and the way into it
    2  Needs you            the five Review Hub queues, with one number each
    3  Where it is slipping the locations nobody is on, and the slowest regions

  **Rule 29: the greeting belongs here and nowhere else.** The line under it is
  what makes it worth the room: it says what today is about rather than only
  saying hello.

  **What is deliberately NOT here.** Your name, the site switcher and Sign Out.
  The owner put them on Home below 1024 on 31 August and reversed it an hour
  later having seen it drawn, which is rule 31: below 1024 the bar says where you
  are and the dock says where you can go, and your account is a place you go.
  They live behind the user block on a desk and behind the dock's You cell below
  1024, both of which already exist.

  **One number, three places.** The badge on the Needs you card, the numbers in
  its rows and the count in the Review Hub are the same five queues added up the
  same way. An earlier draft of the drawing put "Nobody assigned" in this list
  too, and the badge then counted 81 of the 98 rows under it, which is the kind
  of small wrongness that costs trust in every other number on a screen. Nobody
  assigned is on block three instead, because it is not waiting on a decision
  from anybody.
*/

export default function AdminHome({
  profile, cycle, stats, total, pct, cycleValue, notStartedDays,
  regionRows = [], unassignedCount = 0, regionTotal = 0,
  needs = [], onOpenCounts, onCloseCycle, onOpenHub, onOpenRegion, onOpenJobQueue,
}) {
  // Every other list here has an empty one to fall back on and this did not,
  // so a screen that lost its five queues took the whole app down rather than
  // drawing a card with nothing in it. Nothing passes it undefined today; the
  // point is that nothing can.
  const needTotal = needs.reduce((s, n) => s + n.n, 0);
  const due = dueState(cycle?.due_on);

  /*
    **The "days before close" warning, built 5 September 2026 on the owner's
    decision.** Site Settings has had that box since it was written, it saved
    correctly, and nothing read it.

    **It is worked out here rather than stored as a notification, and that is a
    deliberate choice worth defending.** A stored notification needs something to
    send it, and OnHand has nothing that runs on a timer: no scheduler, no
    nightly job. A notification with nothing to trigger it is precisely the kind
    of thing "Stop the silent failures" exists to remove, so building one would
    have been adding the fault while claiming to fix it.

    Worked out on the screen, it is right every time it is looked at, and it
    needs nothing to remember to run.

    **It could not have been built at all before today**, because it measures
    against a finish date and a count did not have one until "Where the app
    opens", earlier the same day.
  */
  const chasing = (due && notStartedDays && due.days >= 0 && due.days <= notStartedDays
    && stats.not_started > 0)
    ? { n: stats.not_started, days: due.days }
    : null;

  // **The line under the greeting says what today is about.** It is built from
  // the same three numbers the blocks below show, so it can never disagree with
  // them: how far the quarter has got, how much is waiting, and how much nobody
  // is on.
  const line = cycle
    ? `${today()}. The quarter is ${pct}% counted, ${needTotal} thing${needTotal === 1 ? '' : 's'} ${needTotal === 1 ? 'is' : 'are'} waiting on you, and ${unassignedCount} location${unassignedCount === 1 ? '' : 's'} still ${unassignedCount === 1 ? 'has' : 'have'} nobody on ${unassignedCount === 1 ? 'it' : 'them'}.${due ? ' ' + due.line : ''}`
    : `${today()}. No count cycle is open.`;

  const headline = cycle ? (
    <Card>
      <Kind>The open cycle</Kind>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', margin: '8px 0 14px' }}>
        <div className="oh-title" style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.4px' }}>{cycle.name}</div>
        <span className="pill pill-app">Open</span>
        <DueChip due={due} />
        <span style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <Kind>Done</Kind>
          <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--action-text)', lineHeight: 1.05 }}>{pct}%</div>
        </div>
      </div>
      <FourBar notStarted={stats.not_started} inProgress={stats.in_progress}
        submitted={stats.submitted} approved={stats.approved} />
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 14, lineHeight: 1.55 }}>
        {total} location{total === 1 ? '' : 's'}
        {cycle.created_at ? `, opened ${dayShort(cycle.created_at)}` : ''}
        {cycleValue != null ? `. Estimated value $${Number(cycleValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}.` : '.'}
      </div>
      {chasing && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, padding: '11px 13px', borderRadius: 'var(--oh-radius-control)', background: 'var(--warning-bg)' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--warning-ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div style={{ fontSize: 12.5, color: 'var(--warning-ink)', fontWeight: 600, lineHeight: 1.5 }}>
            <b>{chasing.n} location{chasing.n === 1 ? ' has' : 's have'} not been started</b> and the count is due
            {chasing.days === 0 ? ' today' : ' in ' + chasing.days + ' day' + (chasing.days === 1 ? '' : 's')}.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" style={{ height: 44 }} onClick={onOpenCounts}>Open Inventory Counts</button>
        <button className="btn btn-outline" style={{ height: 44 }} onClick={onCloseCycle}>Close cycle</button>
      </div>
    </Card>
  ) : (
    // **Rule 9's shape, and it is the screen an admin actually meets between
    // quarters.** It says what is not there, why, and the one way forward.
    <Card pad="32px 24px" style={{ textAlign: 'center' }}>
      <div className="oh-title" style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>No count cycle is open</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.6, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
        Nobody can count until one is. Opening a cycle makes a sheet for every
        location and puts it on the rep who covers it.
      </div>
      <button className="btn btn-primary" style={{ height: 44, marginTop: 18 }} onClick={onOpenCounts}>Open a cycle</button>
    </Card>
  );

  const needsCard = (
    <NeedsCard title="Needs you" count={needTotal} footer={<>Open the Review Hub <ArrowRight size={13} /></>}
      onFooter={() => onOpenHub()}
      rows={needs.map(n => (
        <Need key={n.key} n={n.n} title={n.title} sub={n.sub} tone={n.tone}
          onClick={() => onOpenHub(n.key)} />
      ))} />
  );

  const slipping = (
    <Card>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>Where the quarter is slipping</div>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>
          the {regionRows.length} slowest of {regionTotal}
        </span>
      </div>
      <div style={{ marginTop: 4 }}>
        <Need n={unassignedCount} title="Nobody assigned"
          sub="Locations with no rep on them at all" tone="var(--text-secondary)"
          onClick={() => onOpenRegion('__unassigned')} />
        {regionRows.map(r => (
          <Need key={r.name} n={`${r.pct}%`} title={r.name} live
            sub={`${r.total} locations, ${r.notStarted} not started`}
            tone="var(--info-ink)" onClick={() => onOpenRegion(r.name)} />
        ))}
      </div>
    </Card>
  );

  /*
    **How many ordering jobs are waiting**, asked for by the owner on 3 October
    2026: *"I would also like a card added to the home screen that tells me how
    many jobs are in the queue."*

    **It counts what he could pick up**, so jobs OnHand is still reading and
    jobs it could not read are left out of it. Real from 4 October 2026; it was
    a made-up number for one day while the queue was a drawing, and said so.

    **It is read once when Home is drawn**, not kept live. A number that is a
    minute old is honest; a number that needs something to keep it fresh is a
    thing that can fail quietly, which this project has enough of.
  */
  const [queueCount, setQueueCount] = useState(0);
  useEffect(() => {
    let alive = true;
    countWaiting().then(r => { if (alive && !r.error) setQueueCount(r.count); });
    return () => { alive = false; };
  }, []);

  const queueCard = (
    <Card>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>Orders waiting</div>
        <span style={{ flex: 1 }} />
      </div>
      <button type="button" onClick={() => onOpenJobQueue && onOpenJobQueue()}
        style={{
          display: 'flex', alignItems: 'baseline', gap: 12, width: '100%', marginTop: 10,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          font: 'inherit', textAlign: 'left', minHeight: 44,
        }}>
        <span style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.05, color: 'var(--action-text)' }}>
          {queueCount}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          jobs in the queue you can pick up.<br />Open the Job Queue
          <ArrowRight size={12} />
        </span>
      </button>
    </Card>
  );

  return (
    <div>
      {/* **What changed in OnHand, above the greeting.** The owner's own
          instruction of 5 September 2026: the release note stops taking the
          whole screen and becomes a notice here with one button that clears
          it. It draws nothing at all when there is no unread note, which is
          almost always. Group H's drawing, and it changes this approved
          Group C screen because he asked for it. */}
      <ReleaseNote />
      <Greeting name={profile?.full_name} line={line} />
      <HomeFrame headline={headline} needs={<>{queueCard}{needsCard}</>} third={slipping} />
    </div>
  );
}
